import { useState, useEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { getNavigationConfig } from '@/lib/navigation';
import { useNavigate } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import Shepherd from 'shepherd.js';
import { disposeTour, isTourInProgress, scheduleTour } from '@/lib/tourLifecycle';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import 'shepherd.js/dist/css/shepherd.css';
import '@/styles/tours.css';
import { ChevronRight, Users, BookOpen, Trophy, CheckCircle2, MessageSquare, Link2, FileText, Settings } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sidebar tour steps - top menus first, then key sub-menus, then settings
const SIDEBAR_TOUR_STEPS = [
  // Top-level menus
  {
    id: 'conversations',
    selector: '[data-testid="sidebar-conversations"]',
    title: 'Conversations',
    description: 'Review saved conversations, topic trends and answer feedback. Available views depend on your access.',
    icon: MessageSquare,
    isTopLevel: true,
  },
  {
    id: 'people',
    selector: '[data-testid="sidebar-people"]',
    title: 'People',
    description: 'Review employee adoption. Depending on your role, you can also manage employee records, groups and content-team access.',
    icon: Users,
    isTopLevel: true,
  },
  {
    id: 'knowledge-base',
    selector: '[data-testid="sidebar-knowledge-base"]',
    title: 'Knowledge Base',
    description: 'Review your workspace knowledge and coverage. Eva retrieves relevant content from published, accessible documents to help answer questions.',
    icon: BookOpen,
    isTopLevel: true,
  },
  {
    id: 'engagement',
    selector: '[data-testid="sidebar-engagement"]',
    title: 'Engagement',
    description: 'Review participation on the leaderboard. Notifications and reports are available to roles with the required access.',
    icon: Trophy,
    isTopLevel: true,
  },
  {
    id: 'connectors',
    selector: '[data-testid="nav-connectors"]',
    title: 'Connectors',
    description: 'This is where you can explore and configure available connectors. More connections to your existing tools are in development; look for Coming Soon labels to see what is planned.',
    icon: Link2,
    isTopLevel: true,
  },
  // Organization Settings (top-right)
  {
    id: 'org-settings',
    selector: '[data-testid="user-menu-trigger"]',
    title: 'Organization Settings',
    description: 'Open the top-right Settings menu for your profile. Organization settings, AI usage, billing and audit logs appear according to your role.',
    icon: Settings,
    isTopLevel: true,
    isHeaderElement: true,
  },
  // Key sub-menus
  {
    id: 'add-content',
    selector: '[data-testid="nav-content"]',
    title: 'Add Documents',
    description: 'Users with content-management access can upload documents, review processing and publish content for retrieval. Uploading alone does not make a document available to employees.',
    icon: FileText,
    isTopLevel: false,
    isKeyAction: true,  // Key action step
    parentToExpand: 'knowledge-base',
    action: 'Review document status and access before publishing.',
  },
  {
    id: 'add-employees',
    selector: '[data-testid="nav-employees"]',
    title: 'Manage Employees',
    description: 'Authorized users can add employee records individually or import a CSV file. Configure product access and account activation before expecting someone to sign in.',
    icon: Users,
    isTopLevel: false,
    isKeyAction: true,  // Key action step
    parentToExpand: 'people',
    action: 'Check each employee’s access and invitation status.',
    evaPeopleContext: true,
  },
  // Final step - Eva People invitation
  {
    id: 'try-eva-people',
    selector: '[data-testid="open-eva-people-button"]',
    mobileSelector: '[data-testid="open-eva-people-button-mobile"]',
    attachOnMobile: true,
    title: 'Try Eva People',
    description: 'Use Eva People in the header to ask a question about your workspace’s available content. Review the answer and its supporting sources.',
    icon: MessageSquare,
    isTopLevel: false,
    isKeyAction: true,
    isHeaderElement: true,
    action: 'Use a familiar topic for your first question.',
  },
];

function getTourTarget(step) {
  if (step.mobileSelector) {
    return [...document.querySelectorAll(`${step.selector}, ${step.mobileSelector}`)]
      .find(element => element.getClientRects().length > 0);
  }
  return document.querySelector(step.selector);
}

// Reuse the sidebar policy so tour access does not drift from navigation.
export function getWelcomeTourSteps(hasPermission = () => false, hasRole = () => false) {
  const navigation = getNavigationConfig(hasPermission, hasRole);
  const paths = new Set(navigation.flatMap(item => [item.path, ...(item.children || []).map(child => child.path)]));
  const labels = new Set(navigation.map(item => item.label));
  const routes = {connectors: '/connectors', 'add-content': '/kb/content', 'add-employees': '/people/employees'};
  return SIDEBAR_TOUR_STEPS.filter(step => routes[step.id]
    ? paths.has(routes[step.id])
    : step.isHeaderElement || labels.has(step.title));
}


export function OnboardingInterstitial({ onComplete }) {
  const { token, hasPermission, hasRole } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [trialConfig, setTrialConfig] = useState(null);
  const tourRef = useRef(null);
  const savingRef = useRef(false);
  const previousFocusRef = useRef(null);
  const [saveError, setSaveError] = useState(null);
  
  // Three practical first-use steps.
  const totalSteps = 3;
  
  // Fetch interstitial status and trial config
  useEffect(() => {
    const checkStatus = async () => {
      if (!token) return;
      
      try {
        const statusRes = await fetch(`${API_URL}/api/onboarding/interstitial-status`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        
        if (statusRes.ok) {
          const status = await statusRes.json();
          if (status.show_interstitial) {
            const configRes = await fetch(`${API_URL}/api/onboarding/trial-config`, {
              headers: { 'Authorization': `Bearer ${token}` },
            });
            
            if (configRes.ok) {
              const config = await configRes.json();
              setTrialConfig(config);
            }
            
            setIsVisible(true);
          }
        }
      } catch (error) {
        console.error('Error checking interstitial status:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkStatus();
  }, [token]);
  
  // Complete onboarding and mark as done
  const completeOnboarding = useCallback(async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaveError(null);
    try {
      const completion = await fetch(`${API_URL}/api/onboarding/complete-interstitial`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!completion.ok) throw new Error('Completion failed');
      const step = await fetch(`${API_URL}/api/onboarding/complete-step`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ step_key: 'welcome' }),
      });
      if (!step.ok) throw new Error('Welcome step failed');
    } catch (error) {
      setSaveError('complete');
      setIsVisible(true);
      return;
    } finally { savingRef.current = false; }
    
    setIsVisible(false);
    navigate('/overview');
    
    if (onComplete) {
      onComplete();
    }
  }, [token, navigate, onComplete]);
  
  // Start Shepherd tour
  const startSidebarTour = useCallback(() => {
    
    if (isTourInProgress()) return false;
    
    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        scrollTo: { behavior: 'smooth', block: 'center' },
        modalOverlayOpeningPadding: 8,
        modalOverlayOpeningRadius: 8,
        classes: 'shepherd-eva-tour',
        popperOptions: {
          modifiers: [{ name: 'offset', options: { offset: [0, 12] } }],
        },
      },
    });
    
    const steps = getWelcomeTourSteps(hasPermission, hasRole).filter(step =>
      getTourTarget(step) ||
      (step.parentToExpand && document.querySelector(`[data-testid="sidebar-${step.parentToExpand}"]`))
    );
    steps.forEach((step, index) => {
      const isLast = index === steps.length - 1;
      let highlightedTarget;
      const clearHighlight = () => {
        highlightedTarget?.classList.remove('shepherd-target-highlight', 'shepherd-key-action-highlight');
        highlightedTarget = null;
      };
      
      // Determine badge type and step class
      let badgeClass = step.isTopLevel ? 'top-level' : 'sub-menu';
      let badgeLabel = step.isTopLevel ? 'Main Menu' : 'Key Action';
      let stepClass = 'shepherd-eva-tour';
      
      // Labels distinguish key actions without introducing a second theme.
      if (step.isKeyAction) {
        badgeClass = 'key-action';
        badgeLabel = 'Key Action';
        stepClass = 'shepherd-eva-tour shepherd-key-action';
      } else if (step.isHeaderElement && !step.isKeyAction) {
        badgeClass = 'settings';
        badgeLabel = 'Settings Menu';
      }
      
      let textContent = `
        <div class="shepherd-step-badge ${badgeClass}">
          ${badgeLabel}
        </div>
        <p style="margin: 0 0 8px 0;">${step.description}</p>
        ${step.action ? `<p class="shepherd-action">${step.action}</p>` : ''}
      `;
      
      // Add Eva People context where relevant
      if (step.evaPeopleContext) {
        textContent += `
          <div class="shepherd-eva-context">
            <p><strong>Eva People</strong> is the employee-facing chat interface. Employees need an active account and Eva People access before they can ask questions.</p>
          </div>
        `;
      }
      
      // Determine attachment position
      const attachPosition = step.isHeaderElement ? 'bottom' : 'right';
      
      tour.addStep({
        id: step.id,
        title: step.title,
        text: textContent,
        // Center mobile sidebar steps; the visible Eva People header control remains a usable target.
        attachTo: window.innerWidth < 768 && !step.attachOnMobile
          ? undefined : { element: () => getTourTarget(step), on: attachPosition },
        classes: stepClass,
        buttons: [
          ...(index > 0 ? [{
            text: 'Back',
            action: tour.back,
            classes: 'shepherd-button-secondary',
          }] : []),
          {
            text: isLast ? 'Get Started' : 'Next',
            action: isLast ? tour.complete : tour.next,
            classes: 'shepherd-button-primary',
          },
        ],
        when: {
          'before-show': () => {
            if (!step.parentToExpand) return;
            const parent = document.querySelector(`[data-testid="sidebar-${step.parentToExpand}"]`);
            if (parent && !document.querySelector(step.selector)) {
              // Render the expanded menu before Shepherd resolves its target.
              // An uncancelled beforeShowPromise can reopen a disposed tour.
              flushSync(() => parent.click());
            }
          },
          show: () => {
            const target = getTourTarget(step);
            highlightedTarget = target;
            if (target) {
              // Use different highlight class for key actions
              const highlightClass = step.isKeyAction ? 'shepherd-key-action-highlight' : 'shepherd-target-highlight';
              target.classList.add(highlightClass);
              target.classList.add('shepherd-target-highlight');
            }
          },
          hide: clearHighlight,
          // Completing or cancelling Shepherd destroys steps without hiding them.
          destroy: clearHighlight,
        },
      });
    });
    
    const onComplete = () => {
      completeOnboarding();
    };
    const onCancel = () => {
      completeOnboarding();
    };

    tour.on('complete', onComplete);
    tour.on('cancel', onCancel);

    tourRef.current = tour;

    return scheduleTour(tour, 300);
  }, [completeOnboarding, hasPermission, hasRole]);
  
  // Skip onboarding
  const skipOnboarding = useCallback(async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaveError(null);
    try {
      const response = await fetch(`${API_URL}/api/onboarding/skip`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ skip_all: true }),
      });
      if (!response.ok) throw new Error('Skip failed');
    } catch (error) {
      setSaveError('skip');
      return;
    } finally { savingRef.current = false; }

    setIsVisible(false);
    navigate('/overview');

    if (onComplete) {
      onComplete();
    }
  }, [token, navigate, onComplete]);
  
  const handleProceedToTour = useCallback(() => {
    if (startSidebarTour()) setIsVisible(false);
  }, [startSidebarTour]);

  // Navigation
  const goNext = useCallback(() => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleProceedToTour();
    }
  }, [currentStep, handleProceedToTour]);

  const goBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isVisible || savingRef.current) return;
      if (e.target?.closest?.('button, a, input, select, textarea, [contenteditable="true"]')) return;
      
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        goNext();
      } else if (e.key === 'ArrowLeft' && currentStep > 0) {
        goBack();

      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, currentStep, goNext, goBack, skipOnboarding]);
  
  // Cleanup tour on unmount
  useEffect(() => {
    return () => {
      if (tourRef.current) {
        tourRef.current.off('complete');
        tourRef.current.off('cancel');
        disposeTour(tourRef.current);
        tourRef.current = null;
      }
    };
  }, []);
  
  if (isLoading || !isVisible) {
    return null;
  }
  

  
  return (
    <Dialog open={isVisible} onOpenChange={open => { if (!open) skipOnboarding(); }}>
      <DialogContent
        data-testid="onboarding-interstitial"
        className="flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-5xl flex-col gap-0 overflow-hidden rounded-lg bg-card p-0 motion-reduce:animate-none"
        closeLabel="Skip onboarding"
        closeClassName="right-2 top-2 flex h-11 w-11 items-center justify-center rounded-md bg-card text-foreground opacity-100 hover:bg-muted"
        onOpenAutoFocus={event => {
          event.preventDefault();
          previousFocusRef.current = document.activeElement;
          document.querySelector('[data-testid="welcome-heading"]')?.focus();
        }}
        onCloseAutoFocus={event => {
          event.preventDefault();
          const previous = previousFocusRef.current;
          const target = previous?.isConnected && previous !== document.body
            ? previous : document.querySelector('[data-testid="user-menu-trigger"]');
          target?.focus();
        }}
        aria-describedby={undefined}
        onInteractOutside={event => event.preventDefault()}
      >
        <div className="shrink-0 border-b px-6 py-4 pr-16 text-sm text-muted-foreground">
          Welcome to Eva · {currentStep + 1} of {totalSteps}
        </div>
        {saveError && <div role="alert" className="shrink-0 border-b p-4 text-sm text-foreground">
          <p>Could not save your welcome progress. Please try again.</p>
          <Button onClick={saveError === 'skip' ? skipOnboarding : completeOnboarding} className="mt-3 min-h-11">Retry save</Button>
        </div>}
        <div className="min-h-0 overflow-y-auto overscroll-contain" data-testid="welcome-scroll-content">
          <WelcomeStep step={currentStep} trialConfig={trialConfig} />
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t p-4 sm:px-6">
          <nav aria-label="Welcome steps" className="flex">
            {Array.from({ length: totalSteps }, (_, i) => (
              <Button key={i} variant={i === currentStep ? 'secondary' : 'ghost'}
                className="h-11 w-11 p-0" onClick={() => setCurrentStep(i)}
                aria-label={`Go to step ${i + 1}`} aria-current={i === currentStep ? 'step' : undefined}>
                {i + 1}
              </Button>
            ))}
          </nav>
          <div className="flex flex-wrap items-center gap-2">
            {currentStep > 0 && <Button variant="ghost" className="min-h-11" onClick={goBack} data-testid="back-btn">Back</Button>}
            <Button className="min-h-11" onClick={currentStep === 2 ? handleProceedToTour : goNext}
              data-testid={currentStep === 2 ? 'proceed-tour-btn' : 'next-btn'}>
              {currentStep === 2 ? 'Show me around' : 'Continue'}<ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const WELCOME_STEPS = [
  {
    title: 'Start with one useful question',
    description: 'Eva retrieves content from your workspace to help answer questions. Start with a familiar subject so you can assess the answer.',
    items: ['Ask a specific question in Eva People.', 'Include a relevant topic, date or event.', 'Use the information available in your workspace.'],
    visual: 'question',
  },
  {
    title: 'Two places to work',
    description: 'Eva People is where employees ask questions. Eva Insights is where your content team reviews usage, feedback and knowledge.',
    items: ['People: ask questions and revisit conversations.', 'Insights: review activity, feedback and content gaps.', 'Your role determines which tools you can access.'],
    visual: 'workspaces',
  },
  {
    title: 'Make your first session count',
    description: 'Check the answer against its sources, ask a follow-up if needed and tell your team whether it was helpful.',
    items: ['Check the document name and relevant passage.', 'Use a follow-up question when you need more detail.', 'Report missing or outdated information in your feedback.'],
    visual: 'checklist',
  },
];

function WelcomeStep({ step, trialConfig }) {
  const content = WELCOME_STEPS[step];
  return <div className="grid md:grid-cols-2 [&>*]:min-w-0">
    <div className="order-2 border-t bg-muted/40 p-6 md:order-1 md:border-r md:border-t-0 md:p-8 flex items-center justify-center">
      <div className="w-full max-w-sm space-y-4" aria-label="Illustrative workflow">
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{step === 0 ? 'Example workflow' : step === 1 ? 'Your workspace' : 'Your first session'}</p>
        {content.visual === 'question' ? <>
          <div className="rounded-lg bg-primary p-5 text-primary-foreground"><MessageSquare className="w-6 h-6 mb-3"/><p className="font-medium">What are the steps in our onboarding guide?</p></div>
          <div className="rounded-lg border bg-card p-5"><p className="font-semibold mb-3">Review the answer</p><div className="h-2 bg-muted rounded w-full mb-2"/><div className="h-2 bg-muted rounded w-3/4 mb-5"/><div className="flex gap-2 items-center text-sm text-foreground"><FileText className="w-4 h-4"/> Open the supporting source</div></div>
        </> : content.visual === 'workspaces' ? <>
          <div className="rounded-lg border bg-card p-6"><MessageSquare className="w-7 h-7 text-primary mb-3"/><h3 className="font-semibold text-lg">Eva People</h3><p className="text-sm text-muted-foreground mt-2">Questions, sources and follow-ups</p></div>
          <div className="rounded-lg border bg-card p-6"><BookOpen className="w-7 h-7 text-primary mb-3"/><h3 className="font-semibold text-lg">Eva Insights</h3><p className="text-sm text-muted-foreground mt-2">Knowledge, activity and feedback</p></div>
        </> : ['Ask about a familiar topic', 'Check the supporting source', 'Share useful feedback'].map((text,i)=><div key={text} className="rounded-lg border bg-card p-5 flex items-center gap-4"><span className="w-9 h-9 shrink-0 rounded-full bg-muted text-foreground flex items-center justify-center font-semibold">{i+1}</span><span className="font-medium">{text}</span></div>)}
      </div>
    </div>
    <div className="order-1 p-6 md:order-2 md:p-8">
      <DialogTitle asChild><h1 data-testid="welcome-heading" tabIndex={-1} className="text-2xl sm:text-3xl font-semibold leading-tight tracking-tight mb-4">{content.title}</h1></DialogTitle>
      <p className="text-muted-foreground mb-6">{content.description}</p>
      {step === 0 && trialConfig?.subscription_status === 'trial' && Number.isFinite(trialConfig.duration_days) && <p className="rounded-xl border p-3 mb-5 text-sm">Your trial lasts {trialConfig.duration_days} days.{Number.isFinite(trialConfig.days_remaining) ? ` ${trialConfig.days_remaining} days remaining.` : ''}</p>}
      <ul className="space-y-4">{content.items.map(text=><li key={text} className="flex gap-3 text-sm"><CheckCircle2 className="w-5 h-5 shrink-0 text-primary"/><span>{text}</span></li>)}</ul>

    </div>
  </div>;
}

export default OnboardingInterstitial;
